import AccessTokenStore from '../../lib/accessTokenStore'
import AuthClient from '../../lib/authClient'
import reportAuthFailure from '../../lib/reportAuthFailure'

const EXCHANGE_PATH = 'api/v1/enterprise/auth'

// Longer than the client's usual limit, because the server can be slow to
// answer when the identity provider is, and an exchange abandoned before it
// answers has still spent the token. Waiting longer costs a slow sign-in;
// giving up early costs the session.
const EXCHANGE_TIMEOUT_MS = 30_000


/**
 * Trades a one-time flow token for access and refresh tokens and stores both.
 *
 * A flow token is a short-lived sign-in credential the server issues for
 * somebody it has already authenticated another way, so that a page can sign
 * them in with no password and no cookie. It is spent on first use.
 *
 * The exchange is asked for the form of response that sets no cookies, so
 * everything it returns is held in memory, and the refresh token goes in the
 * body of each refresh.
 *
 * Never retried, whatever the failure: the token is single-use, so a second
 * attempt is refused even when the first never completed. A refusal is the
 * ordinary way for this to fail, since the link that carried the token may
 * have sat unused past its lifetime or been followed twice, so only other
 * failures are reported.
 *
 * @param {String} flowToken
 *
 * @returns {Promise} resolves with whether the tokens were stored, and rejects
 *   only if reporting a failure throws
 */
export default async function exchangeFlowToken(flowToken) {
  try {
    const { data } = await AuthClient.post(EXCHANGE_PATH, {
      context: 'iframe',
      flow_token: flowToken,
    }, {
      timeout: EXCHANGE_TIMEOUT_MS,
    })

    // Storing the access token is what notifies subscribers, and the refresh
    // they schedule reads these, so they go in first.
    AccessTokenStore.setRefreshCredentials({
      clientID: data.client_id,
      refreshToken: data.refresh_token,
    })

    AccessTokenStore.setAccessToken({
      accessToken: data.access_token,
      expiresIn: data.expires_in,
    })

    return true
  } catch (error) {
    reportAuthFailure(error)

    return false
  }
}
